import validSvc from '../validSvc';
import sessionRepo from '../repo/sessionRepo';
import { TblView, TblCol } from './table.types';
import utilSvc from '../utilSvc';
import { LegacyTableView, LegacyTableViewStorage, TblViewSpec, TblViewStorage } from './table-view.types';
import { convertToLegacyTableViews, legacyToTblViewSpec, tblSpecParseToView } from './utils/tableViewHelpers';
import userPrefsRepo from '../repo/userPrefsRepo';
import { ngDelegate } from '../services/ng.delegate';

/**
 * Loads saved custom views from session storage and validates them against the column map.
 * Always starts with a guaranteed default view including all columns.
 * Converts legacy views to TblViewSpec for UI compatibility.
 * @param key Storage key for the table views
 * @param colMap Map of column keys to TblCol objects
 * @param defaultView The default TblViewSpec
 * @returns TblViewStorage containing the current view and all valid views
 */
export function tblSpecLoadStorage(key: string, colMap: Map<string, TblCol>, defaultView: TblViewSpec): TblViewStorage {
    const result: TblViewStorage = {
        current: defaultView.key,
        views: [defaultView], // Always start with fresh default
    };

    // Load saved views from session storage
    try {
        const savedData: any = sessionRepo.get(key);

        if (savedData && Array.isArray(savedData.views)) {
            // Convert legacy views to TblViewSpec if needed
            const asTblViewSpec = (view: any): TblViewSpec | null => {
                if (view && typeof view === 'object' && 'sections' in view && Array.isArray(view.sections)) {
                    // Already TblViewSpec
                    return view as TblViewSpec;
                } else if (view && typeof view === 'object' && 'cols' in view) {
                    // LegacyTableView
                    return legacyToTblViewSpec(view);
                }
                return null;
            };
            const convertedViews = savedData.views.map(asTblViewSpec).filter(Boolean);

            // Validate columns in all sections for each TblViewSpec
            const validatedViews = convertedViews.map(view => {
                // If this is a default view (by key), always overwrite with canonical default
                if (view.key === defaultView.key || view.key === 'default' || view.key === '1') {
                    return defaultView;
                }
                const validatedSections = view.sections.map((section: any) => ({
                    ...section,
                    cols: Array.isArray(section.cols)
                        ? section.cols.filter((colName: string) => {
                              const isValid = colMap.has(colName);
                              if (!isValid) {
                                  console.warn(`⚠️ Column '${colName}' not found in colMap for view '${view.title}'`);
                              }
                              return isValid;
                          })
                        : [],
                }));
                // Ensure pgsize is set to 50 if missing or invalid
                const pgsize = typeof view.pgsize === 'number' && view.pgsize > 0 ? view.pgsize : 50;
                return {
                    ...view,
                    sections: validatedSections,
                    pgsize,
                };
            });

            // Add validated views to result (after default view)
            const nonDefaultViews = validatedViews.filter(view => view.key !== defaultView.key);
            result.views.push(...nonDefaultViews);

            // Set current view from saved data, but validate it exists
            if (savedData.current) {
                // eslint-disable-next-line no-console
                const viewExists = result.views.some(v => v.key === savedData.current);
                if (viewExists) {
                    result.current = savedData.current;
                } else {
                    const isDefaultView =
                        savedData.current === defaultView.key ||
                        savedData.current === 'default' ||
                        savedData.current === '1';

                    if (isDefaultView) {
                        result.current = savedData.current;
                    } else {
                        console.warn(
                            `⚠️ View '${savedData.current}' not yet loaded, using default temporarily. This usually resolves automatically.`,
                        );
                        result.current = defaultView.key;
                    }
                }
            }
        }
    } catch (error) {
        console.error('❌ Error loading saved views from session storage:', error);
    }

    return result;
}

/**
 * Loads a view from a list of TblViewSpecs and returns a TblView for rendering.
 * Ensures the default view always includes all columns.
 * @param id The key of the view to load
 * @param views Array of TblViewSpec
 * @param colMap Map of column keys to TblCol objects
 * @param getDefaultView (optional) Function to get the runtime default view for this table (by default, uses the first view)
 * @returns TblView or undefined if not found/invalid
 */
export function tblSpecLoadView(
    id: string,
    views: TblViewSpec[],
    colMap: Map<string, TblCol>,
    getDefaultView?: () => TblViewSpec,
): TblView | undefined {
    if (!validSvc.isText(id) || views.length < 1 || colMap.size < 1) {
        return undefined;
    }

    let foundViewSpec = views.find(v => v.key == id);

    // BULLETPROOF: If this is a default view request, always use the runtime default from getDefaultView if provided
    if (foundViewSpec && (foundViewSpec.key === 'default' || foundViewSpec.key === '1')) {
        if (typeof getDefaultView === 'function') {
            foundViewSpec = getDefaultView();
        } else {
            foundViewSpec = views[0];
        }
    }

    if (!foundViewSpec) {
        console.warn('[tblSpecLoadView] No view spec found for id:', id);
        return undefined;
    }

    // IMPORTANT: Sort direction is always encoded in the sortby string.
    // Example: sortby: "created" (ascending), sortby: "-created" (descending)
    // Do NOT add a separate sortdir property to legacy format.
    // All code must consistently encode/decode sort direction in sortby.
    const tblView = tblSpecParseToView(colMap, foundViewSpec);
    return tblView;
}

/**
 * Set the current table view for a given storage key.
 * Updates both user preferences and session storage.
 * No conversion to new format; works with legacy view objects.
 *
 * @param storageKey The table storage key (e.g., "opsFeedsList")
 * @param current The current view key
 * @returns Promise<object> Resolves when the current view is updated
 */
export async function setCurrentTableView(storageKey: string, current: string): Promise<void> {
    if (!validSvc.isText(storageKey) || !validSvc.isText(current)) {
        throw new Error('setCurrentTableView: key / current id required');
    }

    const pending = {
        name: storageKey,
        current: current,
    };

    try {
        // first get latest prefs from db
        let userPrefsResponse: any[] = [];
        try {
            userPrefsResponse = await userPrefsRepo.getUserPrefs();
        } catch (getUserPrefsError) {
            userPrefsResponse = [];
        }

        const userPrefs = utilSvc.user.parseLoginProfile({ Profile: userPrefsResponse } as any);
        const saveKey = pending.name;
        const views = userPrefs.TableViews as any[];

        // find this key in table views
        for (let i = 0; i < views.length; i++) {
            if ((views[i] as any).name === saveKey) {
                (views[i] as any).current = pending.current;
                break;
            }
        }

        // Also update session storage to keep it in sync
        const sessionData = sessionRepo.get(storageKey);
        if (sessionData) {
            sessionData.current = pending.current;
            sessionRepo.set(storageKey, sessionData);
        }

        await userPrefsRepo.saveUserPrefs('TABLEVIEW', JSON.stringify(views));
    } catch (error) {
        throw error;
    }
}

/**
 * Deletes a table view by ID and saves the updated views list in legacy format.
 * Filters out the deleted view and the default view, converts to TblViewSpec, and persists.
 * @param tableViews Array of LegacyTableView objects
 * @param viewId The ID of the view to delete
 * @param feedsListTableId The table storage key
 * @param saveTableView Function to persist the updated views
 * @returns Promise<void>
 */
export async function deleteAndSaveTableViews({
    viewId,
    feedsListTableId,
}: {
    viewId: string;
    feedsListTableId: string;
}): Promise<void> {
    // Fetch latest TABLEVIEW array from user preferences
    let userPrefsResponse: any[] = [];
    try {
        userPrefsResponse = await userPrefsRepo.getUserPrefs();
    } catch (getUserPrefsError) {
        userPrefsResponse = [];
    }
    const userPrefs = utilSvc.user.parseLoginProfile({ Profile: userPrefsResponse } as any);
    const allTableViews = Array.isArray(userPrefs.TableViews) ? [...userPrefs.TableViews] : [];

    // Remove the deleted view from the relevant table
    const idx = allTableViews.findIndex((v: any) => v && v.name === feedsListTableId);
    if (idx !== -1) {
        // Remove the view from this table's views
        const legacyStorage = allTableViews[idx] as LegacyTableViewStorage;
        const remainingViews: LegacyTableView[] = Array.isArray(legacyStorage.views)
            ? (legacyStorage.views as LegacyTableView[]).filter(v => v && v.id !== viewId)
            : [];
        // Only save custom views (excluding default with id '1')
        const customViews: LegacyTableView[] = remainingViews.filter(v => v && v.id !== '1');
        // Convert all views to TblViewSpec for saving using utility
        const savedViewsSpec: TblViewSpec[] = customViews.map(legacyToTblViewSpec);
        // Update the storage object for this table
        allTableViews[idx] = {
            ...legacyStorage,
            views: convertToLegacyTableViews(savedViewsSpec),
            current: '1',
        };
    }
    // Save the full array back to user preferences
    await userPrefsRepo.saveUserPrefs('TABLEVIEW', JSON.stringify(allTableViews));
}

// Helper to handle saving/creating a table view
/**
 * Helper to handle saving/creating a table view.
 * Always preserves the Default view and converts all views to legacy-compatible TblViewSpec for saving.
 * @param tableViews Array of LegacyTableView objects
 * @param savedView The view to save (LegacyTableView)
 * @param flyOutMode 'CREATE' or 'EDIT'
 * @param feedsListTableId Storage key for the table views
 * @param saveTableView Function to persist the updated views
 * @returns Promise resolving to the updated LegacyTableViewStorage
 */
export async function createAndSaveTableView({
    savedView,
    flyOutMode,
    feedsListTableId,
}: {
    savedView: LegacyTableView;
    flyOutMode: 'CREATE' | 'EDIT';
    feedsListTableId: string;
}): Promise<LegacyTableViewStorage | void> {
    // Fetch latest TABLEVIEW array from user preferences
    let userPrefsResponse: any[] = [];
    try {
        userPrefsResponse = await userPrefsRepo.getUserPrefs();
    } catch (getUserPrefsError) {
        userPrefsResponse = [];
    }
    const userPrefs = utilSvc.user.parseLoginProfile({ Profile: userPrefsResponse } as any);
    const allTableViews = Array.isArray(userPrefs.TableViews) ? [...userPrefs.TableViews] : [];

    // Find or add the entry for the current table
    const idx = allTableViews.findIndex((v: any) => v && v.name === feedsListTableId);
    const legacyStorage: LegacyTableViewStorage =
        idx !== -1
            ? (allTableViews[idx] as LegacyTableViewStorage)
            : { name: feedsListTableId, current: '1', views: [] };
    // Only persist custom views (never the Default view, id === '1')
    const customViews: LegacyTableView[] = Array.isArray(legacyStorage.views)
        ? (legacyStorage.views as LegacyTableView[]).filter(v => v && v.id !== '1')
        : [];

    // Distinct CREATE/EDIT logic
    if (flyOutMode === 'CREATE') {
        // Prevent duplicate IDs
        const exists = customViews.some(v => v.id === savedView.id);
        if (exists) {
            ngDelegate.error(
                'Duplicate Saved View ID',
                undefined,
                undefined,
                `A view with id '${savedView.id}' already exists. Cannot create duplicate.`,
            );
            return;
        }
        // Add new view
        customViews.push(savedView);
    } else if (flyOutMode === 'EDIT') {
        // Replace existing view with same id
        const editIdx = customViews.findIndex(v => v.id === savedView.id);
        if (editIdx !== -1) {
            customViews[editIdx] = savedView;
        } else {
            // If not found, treat as add (optional: throw error instead)
            customViews.push(savedView);
        }
    }

    // Only save custom views (Default is always regenerated at runtime)
    const customViewsSpec: TblViewSpec[] = customViews.map(legacyToTblViewSpec);
    const updatedStorage: LegacyTableViewStorage = {
        ...legacyStorage,
        views: convertToLegacyTableViews(customViewsSpec),
        current: savedView.id,
    };
    if (idx !== -1) {
        allTableViews[idx] = updatedStorage;
    } else {
        allTableViews.push(updatedStorage);
    }
    // Save the full array back to user preferences
    await userPrefsRepo.saveUserPrefs('TABLEVIEW', JSON.stringify(allTableViews));
    return updatedStorage;
}

/**
 * Ensures session storage for table views is initialized from the API (user preferences) if not already present.
 *
 * On first load or after logout/login, this function checks if session storage for the given table is empty.
 * If so, it fetches the user's saved views from the API and populates session storage, ensuring persistence
 * across sessions and devices. Should be called before reading table views from session storage.
 *
 * @param storageKey The table storage key (e.g., "opsFeedsList")
 * @returns Promise<void> Resolves when session storage is initialized (or already present)
 */
export async function initializeSessionViewsStorage(storageKey: string): Promise<void> {
    // Check if session storage already has data
    const existingData = sessionRepo.get(storageKey);
    if (existingData) {
        return;
    }

    try {
        const userPrefsResponse = await userPrefsRepo.getUserPrefs();

        if (userPrefsResponse && Array.isArray(userPrefsResponse)) {
            const userPrefs = utilSvc.user.parseLoginProfile({ Profile: userPrefsResponse } as any);

            if (userPrefs.TableViews && Array.isArray(userPrefs.TableViews)) {
                // Find our table view data in the database
                const tableViewEntry = userPrefs.TableViews.find((tv: any) => tv.name === storageKey) as any;

                if (tableViewEntry && tableViewEntry.views && Array.isArray(tableViewEntry.views)) {
                    // Create the session storage format
                    const sessionData = {
                        name: storageKey,
                        current: '1', // Default to first view
                        views: tableViewEntry.views,
                    };

                    // Populate session storage for future use
                    sessionRepo.set(storageKey, sessionData);
                }
            }
        }
    } catch (error) {
        console.warn('⚠️ Failed to initialize session storage from database:', error);
    }
}

/**
 * Type guard to check if an object is a LegacyTableView.
 * A LegacyTableView must be a non-null object with 'sortby', 'pgsize', and 'cols' properties.
 *
 * @param view The object to check
 * @returns True if the object is a LegacyTableView
 */
export function isLegacyTableView(view: any): view is LegacyTableView {
    return view != null && typeof view === 'object' && 'sortby' in view && 'pgsize' in view && 'cols' in view;
}

/**
 * Type guard to check if an object is a LegacyTableViewStorage.
 * Validates that the object has a non-empty 'views' array of LegacyTableView objects.
 *
 * @param storage The object to check
 * @returns True if the object is a LegacyTableViewStorage
 */
export function isLegacyStorage(storage: any): storage is LegacyTableViewStorage {
    return !!(
        storage != null &&
        typeof storage === 'object' &&
        storage.views &&
        Array.isArray(storage.views) &&
        storage.views.length > 0 &&
        isLegacyTableView(storage.views[0])
    );
}
